import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {api} from '../../api';

const ALL_APPS = [
  {key: 'inventory', name: 'Inventory'},
  {key: 'crm', name: 'CRM'},
  {key: 'accounting', name: 'Accounting'},
  {key: 'vehicles', name: 'Vehicles'},
  {key: 'tasks', name: 'Tasks'},
  {key: 'storage', name: 'Storage'},
  {key: 'hcm', name: 'HCM'},
  {key: 'timesheets', name: 'Timesheets'},
  {key: 'banned-list', name: 'Banned List'},
];

export default function AdminTenantDetail() {
  const {id} = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [tab, setTab] = useState('users');

  // Users state
  const [principals, setPrincipals] = useState([]);
  const [principalId, setPrincipalId] = useState(null);
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [showAddUser, setShowAddUser] = useState(false);

  // Apps state
  const [enabledApps, setEnabledApps] = useState([]);
  const [appsSaving, setAppsSaving] = useState(false);

  useEffect(() => {
    api(`/tenants/${id}`).then(r => r.ok ? r.json() : null).then(setTenant);
    api(`/tenants/${id}/apps`).then(r => r.ok ? r.json() : []).then(setEnabledApps);
    api('/admin/identity/principals').then(r => r.ok ? r.json() : []).then(p => {
      setPrincipals(p);
      const match = p.find(pr => pr.externalId === id);
      if (match) setPrincipalId(match.id);
    });
  }, [id]);

  useEffect(() => {
    if (!principalId) return;
    api(`/admin/identity/principal/${principalId}/users`).then(r => r.ok ? r.json() : []).then(setUsers);
  }, [principalId]);

  const loadAllUsers = () => {
    api('/admin/identity/users').then(r => r.ok ? r.json() : []).then(setAllUsers);
    setShowAddUser(true);
  };

  const assignUser = async (userId) => {
    await api(`/admin/identity/principal/${principalId}/user/${userId}`, {method: 'POST'});
    setShowAddUser(false);
    api(`/admin/identity/principal/${principalId}/users`).then(r => r.ok ? r.json() : []).then(setUsers);
  };

  const removeUser = async (userId) => {
    await api(`/admin/identity/principal/${principalId}/user/${userId}`, {method: 'DELETE'});
    api(`/admin/identity/principal/${principalId}/users`).then(r => r.ok ? r.json() : []).then(setUsers);
  };

  const toggleApp = (key) => {
    setEnabledApps(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const saveApps = async () => {
    setAppsSaving(true);
    await api(`/tenants/${id}/apps`, {method: 'PUT', body: JSON.stringify(enabledApps)});
    setAppsSaving(false);
  };

  if (!tenant) return null;

  const assignedUserIds = new Set(users.map(u => u.userId));
  const availableUsers = allUsers.filter(u => !assignedUserIds.has(u.userId));

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-16">
          <div className="flex items-center gap-4">
            <img src="/images/matterking-logo.png" alt="Matter King" className="h-10 w-auto" />
            <h1 className="text-lg font-bold text-gray-900">Admin</h1>
            <span className="text-gray-400">/</span>
            <span className="text-gray-600">{tenant.name}</span>
          </div>
          <button onClick={() => navigate('/admin')} className="text-gray-600 hover:text-gray-900 text-sm font-medium">&larr; Back</button>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex gap-4 mb-6 border-b">
          <button onClick={() => setTab('users')} className={`pb-2 px-1 text-sm font-medium ${tab === 'users' ? 'border-b-2 border-red-600 text-red-600' : 'text-gray-500 hover:text-gray-700'}`}>Users</button>
          <button onClick={() => setTab('apps')} className={`pb-2 px-1 text-sm font-medium ${tab === 'apps' ? 'border-b-2 border-red-600 text-red-600' : 'text-gray-500 hover:text-gray-700'}`}>Apps</button>
        </div>

        {tab === 'users' && (
          <div>
            {!principalId && <p className="text-gray-500">No identity principal linked to this tenant. The tenant's ID must match a principal's externalId in the identity service.</p>}
            {principalId && (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Users ({users.length})</h3>
                  <button onClick={loadAllUsers} className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700">Add User</button>
                </div>

                {showAddUser && (
                  <div className="bg-white rounded-lg shadow p-4 mb-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-medium text-gray-900">Select a user to add</h4>
                      <button onClick={() => setShowAddUser(false)} className="text-gray-400 hover:text-gray-600 text-sm">Cancel</button>
                    </div>
                    {availableUsers.length === 0 && <p className="text-sm text-gray-500">All realm users are already assigned.</p>}
                    <div className="space-y-1 max-h-60 overflow-y-auto">
                      {availableUsers.map(u => (
                        <button key={u.userId} onClick={() => assignUser(u.userId)}
                          className="w-full text-left px-3 py-2 rounded hover:bg-gray-50 text-sm">
                          <span className="font-medium">{u.firstName} {u.lastName}</span>
                          <span className="text-gray-500 ml-2">{u.username}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="bg-white rounded-lg shadow">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left text-sm text-gray-500">
                        <th className="p-4">Name</th>
                        <th className="p-4">Username</th>
                        <th className="p-4"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(u => (
                        <tr key={u.userId} className="border-b last:border-0 hover:bg-gray-50">
                          <td className="p-4 font-medium text-gray-900">{u.firstName} {u.lastName}</td>
                          <td className="p-4 text-gray-500 text-sm">{u.username}</td>
                          <td className="p-4 text-right">
                            <button onClick={() => removeUser(u.userId)} className="text-sm text-red-600 hover:text-red-800">Remove</button>
                          </td>
                        </tr>
                      ))}
                      {users.length === 0 && (
                        <tr><td colSpan="3" className="p-4 text-center text-gray-500">No users assigned</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {tab === 'apps' && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Enabled Apps</h3>
            <div className="bg-white rounded-lg shadow divide-y">
              {ALL_APPS.map(app => (
                <label key={app.key} className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50">
                  <span className="font-medium text-gray-900">{app.name}</span>
                  <input type="checkbox" checked={enabledApps.includes(app.key)} onChange={() => toggleApp(app.key)}
                    className="h-5 w-5 text-red-600 rounded border-gray-300 focus:ring-red-500" />
                </label>
              ))}
            </div>
            <button onClick={saveApps} disabled={appsSaving}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
              {appsSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
